import React, { useEffect, useRef, useState } from 'react';
interface RevealProps {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}
function observerSupported(): boolean {
  return typeof window !== 'undefined' && typeof window.IntersectionObserver === 'function';
}

/**
 * Fades a block in when it scrolls into view.
 *
 * Safety first: if IntersectionObserver is missing, throws, or never fires,
 * the content is shown anyway. Nothing can ever stay invisible.
 */
export function Reveal({
  children,
  className = '',
  delay = 0
}: RevealProps) {
  const nodeRef = useRef<HTMLDivElement | null>(null);
  const [visible, setVisible] = useState(() => !observerSupported());
  useEffect(() => {
    if (visible) return;
    const node = nodeRef.current;
    if (!node) {
      setVisible(true);
      return;
    }
    let observer: IntersectionObserver | null = null;

    // Hard fallback: never leave a block hidden, even if nothing fires.
    const failSafe = window.setTimeout(() => setVisible(true), 1500);
    try {
      observer = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          setVisible(true);
        }
      }, {
        threshold: 0.05,
        rootMargin: '0px 0px -40px 0px'
      });
      observer.observe(node);
    } catch {
      setVisible(true);
    }
    return () => {
      window.clearTimeout(failSafe);
      if (observer) observer.disconnect();
    };
  }, [visible]);
  return <div ref={nodeRef} className={className} style={{
    opacity: visible ? 1 : 0,
    transform: visible ? 'none' : 'translateY(18px)',
    transition: 'opacity 520ms ease, transform 520ms ease',
    transitionDelay: `${delay}ms`
  }} data-id="element-201" data-mp="1of3pshf5b5t5">
      {children}
    </div>;
}